export const SALES_MARKETS = ['AUS', 'USA'];
/** Every existing pipeline, lead, appointment and person is Australian — that is the column default. */
export const DEFAULT_SALES_MARKET = 'AUS';
/** The request header the web app sends to say which market's CRM the person is working in. */
export const SALES_MARKET_HEADER = 'x-sales-market';
export const SALES_MARKET_LABEL = { AUS: 'Australia', USA: 'USA' };
/**
 * The USA's working clock. Josh, 19 Oct 2026: "his calendar needs to be US hours" — Eastern, 9am–5pm, weekdays. The
 * USA's public booking calendar offers slots inside these hours, and its BDM calendar is drawn in this zone. Australia's
 * clock is the business timezone (BUSINESS_TIMEZONE), unchanged.
 */
export const USA_TIME_ZONE = 'America/New_York';
export const USA_BOOKING_HOURS = { start: 9, end: 17 };
/** Who may change which markets a person is in. Directors only — it is an access decision. */
export const SALES_MARKET_ADMIN_ROLES = ['OPS_ADMIN', 'ADMIN', 'SUPER_ADMIN'];
export function isSalesMarket(v) {
    return typeof v === 'string' && SALES_MARKETS.includes(v);
}
/** A stored or submitted list → known markets only, no repeats, always in SALES_MARKETS order. */
export function normaliseSalesMarkets(v) {
    if (!Array.isArray(v))
        return [];
    return SALES_MARKETS.filter((m) => v.includes(m));
}
/**
 * The sales team — setters (BSC, "SDRs") and BDMs — who always SEE the USA, by role (Jacob, 30 Oct 2026; see the header). A role, not a ticked box, on purpose:
 * a copy on the account has to be kept right by every path that creates a user or changes their roles, and the first one that forgets leaves a new SDR without the
 * switch. Derived like a VA's placements and a client login's client, in `marketsForPerson`, so it is true the moment the role is, and gone the moment it is.
 */
export const USA_SIGHT_ROLES = ['BSC', 'BDM'];
/** Does this role set put the person on the sales team that always sees the USA? */
export function hasUsaSight(roles) {
    return USA_SIGHT_ROLES.some((r) => roles.includes(r));
}
/**
 * The markets a person is in, from who they are — not only from the list a director ticked.
 *
 *   - Staff: their own list (`User.salesMarkets`), plus the USA when they are on the sales team
 *     (USA_SIGHT_ROLES — see the header). No other role opens a market.
 *   - A VA: their own list PLUS the market of every client they are currently placed with. A VA is
 *     not ticked into a market by a director; they are placed with a client, and must be able to
 *     raise a ticket for that client whichever market it is in.
 *   - A client-portal login: its client's market, and only that. The list on the account is the
 *     Australian default every account is born with, which would lock a USA client out of its own
 *     portal.
 *
 * ⛔ DERIVED on every sign-in check, never copied onto the account when a placement is made: a copy
 * has to be kept right by every path that creates, moves or ends a placement, and the first one that
 * forgets leaves a VA able to see a market they no longer work in.
 */
export function marketsForPerson(p) {
    if (p.roles.includes('CLIENT') && p.clientMarket)
        return normaliseSalesMarkets([p.clientMarket]);
    const sight = hasUsaSight(p.roles) ? ['USA'] : [];
    if (p.roles.includes('VA'))
        return normaliseSalesMarkets([...p.own, ...(p.placementMarkets ?? []), ...sight]);
    return normaliseSalesMarkets([...p.own, ...sight]);
}
/**
 * The markets the web is TOLD a signed-in person is in — `/me`, login and refresh — which is what builds the Australia / USA switch. ⛔ Must be the same list the fence uses, or
 * the switch and the fence disagree (a switch with nothing behind it, or a fence that lets someone into a market they have no switch for). It is `marketsForPerson` with only what
 * those payloads have to hand — the ticked list and the roles — so it matches the fence for everyone who is shown a switcher (staff); a VA's placements and a client login's client
 * are not loaded there and have never been in these payloads.
 */
export function signedInMarkets(u) {
    return marketsForPerson({ own: normaliseSalesMarkets(u.salesMarkets), roles: [u.role, ...(u.roles ?? [])] });
}
/**
 * What a request may see, from the person's own list and the market their screen asked for.
 *
 * - Asking for a market that is not on their list is REFUSED, not quietly swapped for one that is:
 *   a USA screen that silently showed Australian leads would be worse than an error.
 * - Asking for nothing: someone in exactly one market is working in it; someone in several is
 *   fenced to all of theirs with no single active market (by-id links keep working outside the CRM).
 */
export function resolveSalesMarket(granted, asked) {
    const allowed = normaliseSalesMarkets([...granted]);
    const want = (asked ?? '').trim().toUpperCase();
    if (!want)
        return { ok: true, allowed, active: allowed.length === 1 ? (allowed[0] ?? null) : null };
    if (!isSalesMarket(want))
        return { ok: false, reason: 'UNKNOWN_MARKET', asked: want };
    if (!allowed.includes(want))
        return { ok: false, reason: 'NOT_GRANTED', asked: want };
    return { ok: true, allowed, active: want };
}
/**
 * The market the web app should open a person in: the one they last chose if it is still theirs,
 * else their first. Null when they are in no market at all.
 */
export function initialSalesMarket(granted, remembered) {
    const allowed = normaliseSalesMarkets([...granted]);
    if (isSalesMarket(remembered) && allowed.includes(remembered))
        return remembered;
    return allowed[0] ?? null;
}
//# sourceMappingURL=salesMarket.js.map